export type Method = "popup" | "redirect" | "gis";
export type Display = "browser" | "standalone" | "fullscreen" | "minimal-ui";
export type Step = "start" | "ok" | "error" | "fallback" | "lost" | "cancel";

const MODES: Display[] = ["standalone", "fullscreen", "minimal-ui"];

/** The display mode of the page; an installed app is not "browser". */
export function displayMode(matches: (query: string) => boolean, iosStandalone = false): Display {
  return MODES.find((mode) => matches(`(display-mode: ${mode})`)) ?? (iosStandalone ? "standalone" : "browser");
}

/** An installed app opens a popup in a custom tab with no opener, so the popup cannot hand back the result. */
export const methodFor = (display: Display): Method => (display === "browser" ? "popup" : "redirect");

export interface GisMoment {
  kind: "not_displayed" | "skipped" | "dismissed";
  reason: string;
}

const USER_CLOSED = new Set(["user_cancel", "tap_outside", "cancel_called", "flow_restarted"]);

/** After the Google prompt: wait for the token, stop when the user closed it, or else use the popup or redirect. */
export function gisNext(moment: GisMoment): "wait" | "cancel" | "fallback" {
  if (moment.kind === "dismissed" && moment.reason === "credential_returned") return "wait";
  return USER_CLOSED.has(moment.reason) ? "cancel" : "fallback";
}

/** Google Identity Services signs in inside the page, so no other tab or app must give back the result. */
export const usesGis = (provider: "google" | "microsoft", clientId: string | undefined) =>
  provider === "google" && Boolean(clientId);

export const sameOrigin = (authDomain: string | undefined, host: string) =>
  Boolean(authDomain) && authDomain!.toLowerCase() === host.toLowerCase();

export interface SigninEvent {
  slug: string;
  provider: "google" | "microsoft";
  method: Method;
  step: Step;
  display: Display;
  auth_host: string;
  same_origin: boolean;
  tenant: string;
  code?: string;
}

const CODE = /^[a-z0-9/_-]{1,64}$/;

/** Only a Firebase error code goes in a log line, never its message, which can hold an email address. */
export function codeOf(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" && CODE.test(code) ? code : "unknown";
}

export const logLine = (event: SigninEvent) =>
  `sign-in ${event.step}: ${event.provider} by ${event.method} for ${event.slug} (tenant ${event.tenant}, ` +
  `display ${event.display}, auth domain ${event.auth_host}${event.same_origin ? "" : " on another origin"})` +
  (event.code ? `, code ${event.code}` : "");
